import type { AllowableValue, ConfigurationFormData, ReportConfiguration } from '../types';
import { isBreakdownReport } from './reportTypeUtils';
import { transformToAPI } from './valueTransformations';

/**
 * Check if a combination is allowed based on allowable values
 */
export const isAllowedCombination = (
  allowableValues: AllowableValue[],
  formData: Partial<ConfigurationFormData>
): { isAllowed: boolean; templateId?: number; message?: string } => {
  if (!formData.platform || !formData.report_type) {
    return { isAllowed: false, message: 'Platform and Report Type are required' };
  }

  const platformAPI = transformToAPI(formData.platform);
  const reportTypeAPI = transformToAPI(formData.report_type);
  const granularityAPI = transformToAPI(formData.granularity_level);
  const cadenceAPI = formData.cadence ? transformToAPI(formData.cadence) : null;
  const metricTypeAPI = formData.metric_type ? transformToAPI(formData.metric_type) : null;

  if (!platformAPI || !reportTypeAPI || !granularityAPI) {
    return { isAllowed: false, message: 'Invalid platform, report type, or granularity' };
  }

  const match = allowableValues.find(av => {
    const platformMatch = av.platform.toLowerCase().replace(/\s+/g, '_').replace(/&/g, 'and') === platformAPI ||
                          transformToAPI(av.platform) === platformAPI;
    const reportTypeMatch = av.report_type.toLowerCase().replace(/\s+/g, '_').replace(/&/g, 'and') === reportTypeAPI ||
                            transformToAPI(av.report_type) === reportTypeAPI;
    const granularityMatch = av.granularity_level.toLowerCase().replace(/\s+/g, '_') === granularityAPI ||
                              transformToAPI(av.granularity_level) === granularityAPI;
    
    if (!platformMatch || !reportTypeMatch || !granularityMatch) return false;

    if (isBreakdownReport(reportTypeAPI)) {
      const metricMatch = !metricTypeAPI || 
                          av.metric_type?.toLowerCase() === metricTypeAPI ||
                          transformToAPI(av.metric_type || '') === metricTypeAPI;
      return metricMatch;
    } else {
      const cadenceMatch = !cadenceAPI || 
                           av.cadence?.toLowerCase().replace(/\s+/g, '_').replace(/-/g, '_') === cadenceAPI ||
                           transformToAPI(av.cadence || '') === cadenceAPI;
      return cadenceMatch;
    }
  });

  if (match) {
    return { isAllowed: true, templateId: match.template_id };
  }

  return { 
    isAllowed: false, 
    message: 'This combination is not supported'
  };
};

const isEffectivelyDeactivated = (config: ReportConfiguration): boolean => {
  if (config.status === 'deactivated') return true;

  const now = new Date();
  if (!config.deactivation_at) return false;

  const deactivationAt = new Date(config.deactivation_at);
  if (deactivationAt > now) return false;

  if (!config.reactivation_at) return true;
  return new Date(config.reactivation_at) > now;
};

/**
 * Find a duplicate configuration (active or deactivated).
 * Deactivated configs are treated as duplicates so users reactivate instead of re-adding.
 */
export const findDuplicateConfiguration = (
  existingConfigs: ReportConfiguration[],
  formData: ConfigurationFormData,
  excludeId?: number
): ReportConfiguration | null => {
  const platformAPI = transformToAPI(formData.platform);
  const reportTypeAPI = transformToAPI(formData.report_type);
  const granularityAPI = transformToAPI(formData.granularity_level);
  const isBreakdown = isBreakdownReport(reportTypeAPI);

  return (
    existingConfigs.find((config) => {
      if (excludeId && config.id === excludeId) return false;
      if (config.brand_id !== formData.brand_id) return false;
      if (config.platform !== platformAPI) return false;
      if (config.report_type !== reportTypeAPI) return false;
      if (config.granularity_level !== granularityAPI) return false;

      if (isBreakdown) {
        const metricTypeAPI = transformToAPI(formData.metric_type);
        return config.metric_type === metricTypeAPI;
      }

      const cadenceAPI = transformToAPI(formData.cadence);
      return config.cadence === cadenceAPI;
    }) ?? null
  );
};

/**
 * Check for duplicate configuration
 */
export const isDuplicateConfiguration = (
  existingConfigs: ReportConfiguration[],
  formData: ConfigurationFormData,
  excludeId?: number
): boolean => {
  return findDuplicateConfiguration(existingConfigs, formData, excludeId) !== null;
};

export const isDuplicateDeactivatedConfiguration = (
  existingConfigs: ReportConfiguration[],
  formData: ConfigurationFormData,
  excludeId?: number
): boolean => {
  const duplicate = findDuplicateConfiguration(existingConfigs, formData, excludeId);
  return Boolean(duplicate && isEffectivelyDeactivated(duplicate));
};

/**
 * Validate deactivation date is in the future
 */
export const isValidDeactivationDate = (date: Date | string): boolean => {
  const deactivationDate = typeof date === 'string' ? new Date(date) : date;
  return deactivationDate > new Date();
};

/**
 * Check if fiscal calendar checkbox should be shown
 */
export const shouldShowFiscalCalendar = (
  reportType: string | null,
  cadence: string | null
): boolean => {
  if (!reportType || !cadence) return false;
  
  const reportTypeAPI = transformToAPI(reportType);
  const cadenceAPI = transformToAPI(cadence);
  
  if (!reportTypeAPI || !cadenceAPI) return false;
  
  const isRF =
    reportTypeAPI === 'reach_and_frequency' ||
    reportTypeAPI.includes('reach_and_frequency');

  return isRF && ['prior_month', 'month_to_date'].includes(cadenceAPI);
};

/**
 * Validate complete form data before submission
 */
export const validateFormData = (
  formData: ConfigurationFormData
): { isValid: boolean; errors: string[] } => {
  const errors: string[] = [];

  if (!formData.brand_id) {
    errors.push('Brand is required');
  }

  if (!formData.platform) {
    errors.push('Platform is required');
  }

  if (!formData.report_type) {
    errors.push('Report Type is required');
  }

  const isBreakdown = isBreakdownReport(formData.report_type);

  if (isBreakdown && !formData.metric_type) {
    errors.push('Metric Type is required for Breakdown reports');
  }

  if (!isBreakdown && formData.report_type && !formData.cadence) {
    errors.push('Cadence is required for Reach & Frequency reports');
  }

  return {
    isValid: errors.length === 0,
    errors,
  };
};
